import { useState } from "react";
import { addWeeks } from "date-fns";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { colorOf } from "@/lib/colors";
import { mondayOf, weekDays, weekLabel } from "@/lib/dates";
import { EmployeeAvatar } from "./EmployeeBadge";

const Section = ({ children }) => <p className="mt-1 text-xs font-medium uppercase tracking-wider text-slate-500">{children}</p>;
const toggleIn = (xs, v) => (xs.includes(v) ? xs.filter((x) => x !== v) : [...xs, v]);
const THIS_MONDAY = () => mondayOf(new Date()).getTime();

const WeekNav = ({ monday, setMonday, testPrefix }) => {
  const step = (n) => setMonday((m) => addWeeks(m, n));
  const now = THIS_MONDAY();
  return (
    <div className="flex items-center gap-2">
      <Button type="button" variant="outline" size="icon" data-testid={`${testPrefix}-week-prev`} disabled={monday.getTime() <= addWeeks(now, -52).getTime()} onClick={() => step(-1)} className="h-11 w-11 shrink-0 rounded-xl" aria-label="Settimana precedente">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <p data-testid={`${testPrefix}-week-label`} className="flex-1 text-center font-display text-sm font-semibold">
        {weekLabel(monday)}
        {monday.getTime() === now && <span className="block text-[11px] font-normal text-slate-500">settimana corrente</span>}
      </p>
      <Button type="button" variant="outline" size="icon" data-testid={`${testPrefix}-week-next`} disabled={monday.getTime() >= addWeeks(now, 52).getTime()} onClick={() => step(1)} className="h-11 w-11 shrink-0 rounded-xl" aria-label="Settimana successiva">
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};

const EmployeePick = ({ employee, active, onClick, testId }) => {
  const c = colorOf(employee);
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className="flex min-h-[48px] items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm font-medium transition-shadow"
      style={{ background: c.bg, borderColor: active ? c.accent : c.border, boxShadow: active ? `0 0 0 2px ${c.accent}` : "none", color: c.ink }}
    >
      <EmployeeAvatar employee={employee} size={26} />
      <span className="min-w-0 flex-1 truncate">{employee.name}</span>
      {active && <Check className="h-4 w-4 shrink-0" />}
    </button>
  );
};

// Pick several employees and several days, across any week; selected days stay selected when changing week
export const SlotPicker = ({ employees, empIds, setEmpIds, dates, setDates, startMonday, sourceDate, testPrefix }) => {
  const [monday, setMonday] = useState(startMonday);
  const days = weekDays(monday);
  const outside = dates.filter((d) => !days.some((x) => x.iso === d)).length;
  return (
    <div className="col-span-2 space-y-2">
      <Section>Settimana</Section>
      <WeekNav monday={monday} setMonday={setMonday} testPrefix={testPrefix} />
      <Section>Giorni (anche più di uno)</Section>
      <div className="grid grid-cols-3 gap-2">
        {days.map((d) => (
          <button
            key={d.iso}
            type="button"
            data-testid={`${testPrefix}-day-${d.idx}`}
            aria-pressed={dates.includes(d.iso)}
            onClick={() => setDates((ds) => toggleIn(ds, d.iso))}
            className={`day-chip w-full ${dates.includes(d.iso) ? "is-active" : ""}`}
          >
            <span className="font-display text-sm font-semibold">{d.short}</span>
            <span className="text-[11px] opacity-70">{d.iso === sourceDate ? "originale" : d.label}</span>
          </button>
        ))}
      </div>
      {outside > 0 && (
        <p data-testid={`${testPrefix}-other-weeks`} className="text-[11px] text-slate-500">
          + {outside} {outside === 1 ? "giorno scelto" : "giorni scelti"} in altre settimane
        </p>
      )}
      <Section>Dipendenti (anche più di uno)</Section>
      <div className="grid grid-cols-2 gap-2">
        {employees.map((e) => (
          <EmployeePick key={e.id} employee={e} active={empIds.includes(e.id)} onClick={() => setEmpIds((xs) => toggleIn(xs, e.id))} testId={`${testPrefix}-employee-${e.id}`} />
        ))}
      </div>
    </div>
  );
};
